import React from 'react';
import './UserDetailsModal.css';

function formatNumber(value, digits = 2) {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed)) return '0';
    return parsed.toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
}

function formatPercent(value) {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed)) return '0.00%';
    const prefix = parsed > 0 ? '+' : '';
    return `${prefix}${parsed.toFixed(2)}%`;
}

function pnlClass(value) {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed) || parsed === 0) return 'user-details-pnl--neutral';
    return parsed > 0 ? 'user-details-pnl--positive' : 'user-details-pnl--negative';
}

function Stat({ label, value, className = '' }) {
    return (
        <div className="user-details-stat">
            <span className="user-details-stat-label">{label}</span>
            <span className={`user-details-stat-value ${className}`.trim()}>{value}</span>
        </div>
    );
}

/**
 * props:
 * - data
 * - loading
 * - error
 */
function UserDetailsModal({ data, loading, error }) {
    const subscription = data?.subscription || {};
    const usage = data?.usage || {};
    const trading = data?.trading || {};

    return (
        <div className="modal fade" id="modalUserDetails" tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content user-details-modal">
                    <div className="modal-header">
                        <p className="modal-title">User details</p>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close" />
                    </div>
                    <div className="modal-body">
                        {loading ? <p className="user-details-status">Loading user details…</p> : null}
                        {!loading && error ? <p className="user-details-status user-details-status--error">{error}</p> : null}

                        {!loading && !error && data ? (
                            <>
                                <div className="user-details-identity">
                                    <div>
                                        <h3 className="user-details-name">{data.name || 'Unnamed user'}</h3>
                                        <p className="user-details-email">{data.email}</p>
                                    </div>
                                    <span className={`user-details-status-badge ${data.isActive ? 'is-active' : 'is-inactive'}`}>
                                        {data.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>

                                <section className="user-details-section">
                                    <h4>Subscription</h4>
                                    <div className="user-details-grid">
                                        <Stat label="Plan" value={subscription.name || '—'} />
                                        <Stat label="Futures" value={subscription.hasFutures ? 'Enabled' : 'Not included'} />
                                        <Stat label="Max bots" value={subscription.maxAutomations ?? 0} />
                                        <Stat label="Max monitors" value={subscription.maxMonitors ?? 0} />
                                        <Stat label="Max backtests" value={subscription.maxBacktests ?? 0} />
                                    </div>
                                </section>

                                <section className="user-details-section">
                                    <h4>Activity</h4>
                                    <div className="user-details-grid">
                                        <Stat label="Active bots" value={`${usage.activeBots ?? 0} / ${usage.totalBots ?? 0}`} />
                                        <Stat label="Active monitors" value={`${usage.activeMonitors ?? 0} / ${usage.totalMonitors ?? 0}`} />
                                        <Stat label="Algo bots" value={`${usage.activeStrategies ?? 0} / ${usage.totalStrategies ?? 0}`} />
                                    </div>
                                </section>

                                <section className="user-details-section">
                                    <h4>Trading</h4>
                                    <div className="user-details-grid">
                                        <Stat label="Filled orders" value={trading.filledOrders ?? 0} />
                                        <Stat label="Total trades" value={trading.totalTrades ?? 0} />
                                        <Stat label="Open trades" value={trading.activeTrades ?? 0} />
                                        <Stat label="Closed trades" value={trading.exitedTrades ?? 0} />
                                        <Stat
                                            label="Profit / loss"
                                            value={formatNumber(trading.totalProfitLoss)}
                                            className={pnlClass(trading.totalProfitLoss)}
                                        />
                                        <Stat
                                            label="P&amp;L %"
                                            value={formatPercent(trading.profitLossPercent)}
                                            className={pnlClass(trading.profitLossPercent)}
                                        />
                                    </div>
                                </section>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UserDetailsModal;
